import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { CircleUser, Home, LogOut, Building2, LayoutDashboard } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { PAGE_GUTTERS, PAGE_MAX_WIDTH, pageBleedXClass } from '@/components/global/PageContainer';

function NavItem({ to, icon: Icon, children }: { to: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 rounded-[1.25rem] px-4 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-neutral-100 text-foreground'
            : 'text-muted-foreground hover:bg-neutral-50 hover:text-foreground'
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {children}
    </NavLink>
  );
}

export function PlatformHeader() {
  const [email, setEmail] = useState<string | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/auth/me');
        if (!res.ok) return;
        const data = await res.json();
        setEmail(data.email || null);
        const roles: string[] = data.roles || [];
        setIsPlatformAdmin(roles.includes('platformAdmin') || roles.includes('admin'));
      } catch {
        // Ignore auth errors
      }
    })();
  }, [location.pathname]);

  async function onLogout() {
    try {
      await fetch('/auth/logout', { method: 'POST' });
    } finally {
      navigate('/');
    }
  }

  if (!isPlatformAdmin) {
    return null;
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full border-b bg-white shadow-sm',
        pageBleedXClass
      )}
    >
      <div
        className={cn(
          'mx-auto flex h-16 w-full min-w-0 items-center justify-between',
          PAGE_MAX_WIDTH,
          PAGE_GUTTERS
        )}
      >
        <Link to="/platform" className="mr-4 flex shrink-0 items-center gap-2">
          <img src="/express_b.svg" alt="Bright.Express" className="h-10 w-10 shrink-0 object-contain" />
          <Badge variant="secondary" className="bg-purple-100 text-purple-700">Platform</Badge>
        </Link>
        <nav className="flex flex-1 items-center justify-center overflow-x-auto overflow-y-hidden md:flex-initial md:overflow-visible">
          <div className="flex min-w-max items-center gap-1">
            <NavItem to="/platform" icon={LayoutDashboard}>
              Dashboard
            </NavItem>
            <NavItem to="/platform/tenants" icon={Building2}>
              Tenants
            </NavItem>
            <NavItem to="/home" icon={Home}>
              Exit Platform
            </NavItem>
          </div>
        </nav>
        <div className="ml-auto flex items-center">
          {email && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-purple-600 text-white shadow-sm outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label="User menu"
                >
                  <CircleUser className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[10rem]">
                <DropdownMenuLabel className="font-semibold">
                  <span className="truncate">{email}</span>
                  <Badge variant="outline" className="ml-2 text-xs">Platform Admin</Badge>
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={onLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
